import { ProjectInputFilter } from "domain/models/project-input-filter";
import Spinner from "presentation/components/Shared/Spinner";
import { useProjectsByFilter } from "presentation/hooks/use-query-project";
import { useFilterProjectStore } from "presentation/stores/use-filter-project-store";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import StatusProjectDropdown from "./StatusProjectDropdown";
import HeaderProjectGrid from "./HeaderProjectGrid";
import { Project } from "domain/entities/project";
import { useProjectStore } from "presentation/stores/use-project-store";
import { formatDateToPT } from "presentation/utils/formatDateToPT";

const ProjectGrid: React.FC = () => {
  const navigate = useNavigate();
  const { filters, setFilters } = useFilterProjectStore();
  const { project, setProject, resetProject } = useProjectStore();
  const toast = useRef<Toast>(null);
  const [lazyParams, setLazyParams] = useState({
    sortBy: "id",
    sortOrder: "desc",
    limit: 50,
    page: 1,
    filters: {},
  });

  const {
    data: timesheets,
    isLoading,
    isError,
    error,
    refetch,
  } = useProjectsByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as ProjectInputFilter);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters, refetch]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  const showActionOptions = (rowData: Project) => {
    return (
      <div>
        <i
          className="pi pi-pencil cursor-pointer"
          onClick={() => navigate(`/projects/edit/${rowData.id}`)}
        />
      </div>
    );
  };

  return (
    <div>
      <DataTable
        value={timesheets?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={timesheets?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderProjectGrid />}
        rowsPerPageOptions={[2, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={["year", "month", "status"]}
      >
        <Column
          field="name"
          header="Nome"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="client.name"
          header="Cliente"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="startAt"
          header="Início"
          sortable
          filter
          showFilterMatchModes={false}
          body={(rowData) => formatDateToPT(rowData.startAt)}
        />
        <Column
          field="endAt"
          header="Término"
          sortable
          filter
          showFilterMatchModes={false}
          body={(rowData) => formatDateToPT(rowData.endAt)}
        />
        <Column
          header="Status"
          sortable
          filter
          filterField="status"
          body={(rowData) => rowData.status}
          showFilterMatchModes={false}
          filterElement={(options) => (
            <StatusProjectDropdown options={options} />
          )}
        />

        <Column
          body={showActionOptions}
          header="Ações"
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default ProjectGrid;
